import type { PlayerAction, RoomView, SeatNo } from '@chipless/shared';
import { adjustDeltaProblem, chipUnitOf } from '@chipless/shared';
import { Alert, Button, Group, Modal, Paper, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { AmountInput, parseAmount, type AmountValue } from '../components/AmountInput.js';
import { pt, ptWithUnit, seatLabel } from '../format.js';
import { useStore } from '../store.js';
import classes from './OperatorPanel.module.css';

/**
 * 卓の運営操作（要件3.2、4.1、10.1、10.6、2.6）。
 *
 * 影響の大きい操作（ハンド無効化、代理アクション）には確認を挟む。
 */
export function OperatorPanel({ view }: { view: RoomView }): React.ReactElement {
  const send = useStore((s) => s.send);
  const pending = useStore((s) => s.pending);
  const [voidOpen, setVoidOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  /** 開くたびに内部状態を作り直すための鍵。前回の選択が残らないようにする。 */
  const [adjustKey, setAdjustKey] = useState(0);
  const [proxySeat, setProxySeat] = useState<SeatNo | null>(null);

  const hand = view.hand;
  const handInProgress = hand !== null && hand.street !== 'COMPLETE';
  const seatedCount = view.seats.filter((s) => s.occupied).length;
  const readyCount = view.seats.filter((s) => s.occupied && s.stack > 0).length;

  /** 手番の席が切断中なら代理アクションが必要になる（要件2.6）。 */
  const disconnectedTurnSeat =
    hand !== null && hand.currentTurn !== null && view.seats[hand.currentTurn]?.connected === false
      ? hand.currentTurn
      : null;

  return (
    <Paper withBorder p="md" radius="md" className={classes.panel}>
      <Stack>
        {/*
          進行中の卓に手を入れる操作を左に集め、ハンドを進める操作だけを右へ離す。
          最も頻繁に押すのが開始で、取り消しや無効化と隣り合わせると誤操作の目が増える。
        */}
        <div className={classes.bar}>
          <Button
            data-testid="op-void"
            variant="light"
            color="red"
            disabled={!handInProgress || pending}
            onClick={() => setVoidOpen(true)}
          >
            ハンドを無効化
          </Button>
          <Button
            data-testid="op-adjust"
            variant="default"
            onClick={() => {
              setAdjustKey((k) => k + 1);
              setAdjustOpen(true);
            }}
            disabled={seatedCount === 0}
          >
            スタックを調整
          </Button>
          <Button
            data-testid="op-undo"
            variant="light"
            disabled={hand === null || !hand.canUndo || pending}
            onClick={() => send({ type: 'hand:undo', handSeq: hand?.handSeq ?? 0 })}
          >
            直前の操作を取り消す
          </Button>
          {/* 幅が足りないときはここで改行し、削除と開始を2段目に置く。 */}
          <div className={classes.break} />
          {/*
            卓を畳む操作。他と並べると誤って押しうるので間隔を空ける。進行中は無効。
            清算していない卓を1操作で到達不能にはしない。
          */}
          <Button
            data-testid="op-close-room"
            className={classes.close}
            variant="light"
            color="red"
            disabled={handInProgress || pending}
            onClick={() => setCloseOpen(true)}
          >
            ルームを削除
          </Button>
          <div className={classes.spacer} />
          <Button
            data-testid="op-start-hand"
            disabled={handInProgress || readyCount < 2 || pending}
            onClick={() => send({ type: 'hand:start' })}
          >
            {/* まだ1ハンドも進んでいない卓で「次の」と言うと、前のハンドがあるように読める。 */}
            {view.handNo === 0 ? 'ハンドを開始' : '次のハンドを開始'}
          </Button>
        </div>

        {readyCount < 2 && !handInProgress && (
          <Text size="sm" c="dimmed">
            開始にはスタックのある席が2席以上必要です（現在 {readyCount} 席）
          </Text>
        )}

        {disconnectedTurnSeat !== null && (
          <Alert color="orange" title="手番のプレイヤーが切断中">
            <Group justify="space-between" wrap="wrap">
              <Text size="sm">
                {view.seats[disconnectedTurnSeat]?.name ?? seatLabel(disconnectedTurnSeat)}{' '}
                の代理でアクションを実行できます。
              </Text>
              <Button size="sm" onClick={() => setProxySeat(disconnectedTurnSeat)}>
                代理で操作する
              </Button>
            </Group>
          </Alert>
        )}
      </Stack>

      <VoidModal
        opened={voidOpen}
        onClose={() => setVoidOpen(false)}
        onConfirm={() => {
          send({ type: 'hand:void' });
          setVoidOpen(false);
        }}
      />

      <CloseRoomModal
        view={view}
        opened={closeOpen}
        onClose={() => setCloseOpen(false)}
        onConfirm={() => {
          send({ type: 'room:close' });
          setCloseOpen(false);
        }}
      />

      <AdjustModal
        key={adjustKey}
        view={view}
        opened={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onConfirm={(seatNo, delta) => {
          send({ type: 'stack:adjust', seatNo, delta });
          setAdjustOpen(false);
        }}
      />

      <ProxyModal
        view={view}
        seatNo={proxySeat}
        onClose={() => setProxySeat(null)}
        onConfirm={(seatNo, action) => {
          send({ type: 'action:proxy', handSeq: hand?.handSeq ?? 0, seatNo, action });
          setProxySeat(null);
        }}
      />
    </Paper>
  );
}

function VoidModal({
  opened,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  return (
    <Modal opened={opened} onClose={onClose} title="ハンドを無効化する" centered>
      <Stack>
        <Text>
          {
            'ミスディール用の操作です。このハンドの拠出額はすべて各席へ返還され、ディーラーボタンも開始前の位置に戻ります。取り消せません。'
          }
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            やめる
          </Button>
          <Button color="red" onClick={onConfirm}>
            無効化する
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * ルームの削除（要件1.9）。
 *
 * 取り消せず、全端末が同時に卓から追い出される。何が起きるかを列挙してから確認させる。
 */
function CloseRoomModal({
  view,
  opened,
  onClose,
  onConfirm,
}: {
  view: RoomView;
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const seated = view.seats.filter((s) => s.occupied).length;
  return (
    <Modal opened={opened} onClose={onClose} title="ルームを削除する" centered>
      <Stack>
        <Text>
          {`ルーム ${view.code} を削除します。着席中の ${seated} 名を含むすべての端末が卓から切断され、このルームコードと QR コードは使えなくなります。取り消せません。`}
        </Text>
        <Text size="sm" c="dimmed">
          記録は残しません。スタックの数字が必要なら、閉じる前に控えてください。
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            やめる
          </Button>
          <Button color="red" onClick={onConfirm} data-testid="op-close-room-confirm">
            削除する
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * スタックの増減（要件3.2）。リバイの主経路。
 *
 * 対象席の初期値は「リバイが必要な席」を優先する。バストした席を足しに来るのが
 * 大半の用途なので、開いた時点でその席が選ばれていると操作が1手減る。
 */
function AdjustModal({
  view,
  opened,
  onClose,
  onConfirm,
}: {
  view: RoomView;
  opened: boolean;
  onClose: () => void;
  onConfirm: (seatNo: SeatNo, delta: number) => void;
}): React.ReactElement {
  const occupied = view.seats.filter((s) => s.occupied);
  const initial =
    occupied.find((s) => s.state === 'NEEDS_REBUY') ??
    occupied.find((s) => s.stack === 0) ??
    occupied[0];

  const [target, setTarget] = useState<string | null>(
    initial === undefined ? null : String(initial.seatNo),
  );
  // 文字列のまま持つ。「-」だけの状態を 0 に戻すと、減算が打てなくなる。
  const [deltaText, setDeltaText] = useState<AmountValue>(view.config.defaultBuyIn);
  const delta = parseAmount(deltaText);

  const selected = target === null ? null : Number(target);
  const seat = selected === null ? undefined : view.seats[selected];
  // 進行中のハンドに参加している席への増減は、次のハンドから有効になる（要件3.3）。
  const deferred =
    seat !== undefined &&
    view.hand !== null &&
    view.hand.street !== 'COMPLETE' &&
    (seat.state === 'ACTIVE' || seat.state === 'ALLIN' || seat.state === 'FOLDED');

  const wouldGoNegative =
    seat !== undefined && delta !== null && delta < 0 && seat.stack + delta < 0;
  // サーバのスタック調整の検証と同じ判定。
  const deltaIssue =
    delta === null ? '増減額を入力してください' : adjustDeltaProblem(view.config, delta);

  return (
    <Modal opened={opened} onClose={onClose} title="スタックを調整" centered>
      <Stack>
        <Select
          label="対象の席"
          data={occupied.map((s) => ({
            value: String(s.seatNo),
            label: `${seatLabel(s.seatNo)} ${s.name}（${ptWithUnit(s.stack)}）`,
          }))}
          value={target}
          onChange={setTarget}
          allowDeselect={false}
          data-testid="adjust-seat"
        />
        <AmountInput
          label="増減額"
          description={`マイナスを入れると減算します（${pt(chipUnitOf(view.config))} 単位）`}
          value={deltaText}
          onChange={setDeltaText}
          step={view.config.bigBlind}
          problem={deltaIssue}
          data-testid="adjust-delta"
        />
        <Group gap="xs">
          {[view.config.bigBlind * 10, view.config.bigBlind * 50, view.config.defaultBuyIn].map(
            (amount) => (
              <Button
                key={amount}
                size="compact-sm"
                variant="light"
                onClick={() => setDeltaText(amount)}
              >
                +{ptWithUnit(amount)}
              </Button>
            ),
          )}
        </Group>
        {deferred && (
          <Alert color="yellow">
            {
              'この席は進行中のハンドに参加中です。増減は次のハンドから有効になり、それまでは「次ハンド +○○」と表示されます。'
            }
          </Alert>
        )}
        {wouldGoNegative && (
          <Alert color="red">スタックが負になります。減算額を小さくしてください。</Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            やめる
          </Button>
          <Button
            data-testid="adjust-confirm"
            disabled={selected === null || deltaIssue !== null || wouldGoNegative}
            onClick={() => selected !== null && delta !== null && onConfirm(selected, delta)}
          >
            反映する
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * 代理アクション。
 *
 * 選択肢は `RoomView` には載らない（`you.legalActions` は受信者自身のもの）。中央からは
 * フォールドとチェックだけを提供する。金額を伴う代理は、本人の意思を推測することになる
 * ため置かない。
 */
function ProxyModal({
  view,
  seatNo,
  onClose,
  onConfirm,
}: {
  view: RoomView;
  seatNo: SeatNo | null;
  onClose: () => void;
  onConfirm: (seatNo: SeatNo, action: PlayerAction) => void;
}): React.ReactElement {
  const hand = view.hand;
  const seat = seatNo === null ? undefined : view.seats[seatNo];
  // コール必要額が残っていなければチェック、残っていればフォールドだけを出す。
  const canCheck = hand !== null && seat !== undefined && hand.currentBet === seat.streetBet;

  const choices: { action: PlayerAction; label: string; color: string }[] = canCheck
    ? [{ action: { type: 'CHECK' }, label: 'チェック', color: 'blue' }]
    : [{ action: { type: 'FOLD' }, label: 'フォールド', color: 'red' }];

  return (
    <Modal opened={seatNo !== null} onClose={onClose} title="切断中の席の代理操作" centered>
      <Stack>
        <Text>
          {seatNo === null ? '' : (view.seats[seatNo]?.name ?? seatLabel(seatNo))}{' '}
          の代理でアクションを実行します。履歴には代理として記録されます。
        </Text>
        <Text size="sm" c="dimmed">
          金額を伴うアクションは代理できません。本人の再接続を待ってください。
        </Text>
        <SimpleGrid cols={1}>
          {choices.map((choice) => (
            <Button
              key={choice.label}
              color={choice.color}
              onClick={() => seatNo !== null && onConfirm(seatNo, choice.action)}
            >
              {choice.label}
            </Button>
          ))}
        </SimpleGrid>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            やめる
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
